<template>
  <div class="wdw-forum-thought" :class="'thought-' + thoughtid">
    <template v-if="!thought">
      <loading loadingMessage="Thema wird geladen..." />
    </template>

    <template v-else>
      <template v-if="topic">
        <div class="thought controls top-controls w-100 align-self-center">
          <!-- <create-thought-button :topic="topic" @create-thought="emitCreateThought"/> -->
          <create-comment-button :thought="thought" :topic="topic" @create-comment="emitCreateComment" />
          <follow-thought-button :thought="thought" />
        </div>
      </template>

      <div class="comment-list">
        <template v-if="thought && currentPage == 1">
          <thought-in-comment-list :thought="thought" :topic="topic" @edit-thought="emitEditThought()" />
        </template>

        <template v-if="isLoading">
          <div class="forum-list-skeleton" v-for="n in rows" v-bind:key="n">
            <Skeleton width="100%" height="3rem" />
          </div>
        </template>
        <template v-else-if="totalCommentsCount == 0">
          <div class="no-comments-found fst-italic">
            <p>Noch keine Antworten vorhanden</p>
          </div>
        </template>

        <template v-else>
          <div v-if="comments.length > 0">
            <comment-in-list v-for="comment in comments" v-bind:key="comment.id" :comment="comment" :topic="topic" />
          </div>
        </template>
      </div>
    </template>

    <!-- leave this outside of "else" template to keep Paginator instance up -->
    <div class="d-flex flex-sm-column flex-md-row">
      <template v-if="thought && topic">
        <div class="thought controls bottom-controls align-self-center">
          <!-- <create-thought-button :topic="topic" @create-thought="emitCreateThought"/> -->
          <create-comment-button :thought="thought" :topic="topic" @create-comment="emitCreateComment" />
          <follow-thought-button :thought="thought" />
        </div>
      </template>
      <div v-if="totalCommentsCount > 0" class="flex-md-grow-1">
        <Paginator :rows="rows" :totalRecords="totalCommentsCount" @page="onPageChange($event)" />
      </div>
    </div>
  </div>
</template>

<script>
import { onBeforeMount, ref, watch, computed, toRef } from 'vue';

// stores
import { userStore } from '@/store/user-store';
import { projectStore } from '@/store/project-store';

// components
import Skeleton from 'primevue/skeleton';
import Paginator from 'primevue/paginator';
import Loading from '@/components/general/Loading.vue';
import CommentInList from '@/components/forum/elements/CommentInList.vue';
import ThoughtInCommentList from '@/components/forum/elements/ThoughtInCommentList.vue';
// import CreateThoughtButton from "@/components/forum/elements/CreateThoughtButton.vue";
import CreateCommentButton from '@/components/forum/elements/CreateCommentButton.vue';
import FollowThoughtButton from '@/components/forum/elements/FollowThoughtButton.vue';

// repositories
import { RepositoryFactory } from '@/api/repositoryFactory';
const ThoughtRepository = RepositoryFactory.get('thought');
const CommentRepository = RepositoryFactory.get('comment');

export default {
  name: 'forum-single-thought',
  props: {
    thoughtid: {
      type: Number,
      required: true,
    },
  },
  emits: [
    'create-comment',
    'create-thought',
    'edit-thought',
    'loaded-thought-title',
    'loaded-topic-title',
    'show-topics',
  ],

  setup(props, context) {
    const thoughtid = toRef(props, 'thoughtid');
    const loadingThought = ref(true);
    const loadingComments = ref(true);
    const rows = 10;
    const currentPage = ref(1);
    const thought = ref(null);
    const topic = ref(null);
    const comments = ref([]);
    const pager = ref({});
    const commentFormElem = ref();

    //
    //-----------------COMPUTED------------------
    //

    const totalCommentsCount = computed(() => {
      return pager.value.total_entries;
    });

    const isLoading = computed(() => {
      return loadingThought.value || loadingComments.value || !topic.value;
    });

    //
    //-----------------METHODS------------------
    //

    function loadComments(pageNummber) {
      loadingComments.value = true;
      currentPage.value = pageNummber;
      const params = {
        rows: rows,
        page: pageNummber,
      };
      CommentRepository.get(thoughtid.value, params)
        .then(result => {
          if (result.data) {
            comments.value = result.data;
            pager.value = result.pager;
          } else {
            console.log('error');
          }
          loadingComments.value = false;
        })
        .catch(error => {
          console.log(error);
          loadingComments.value = false;
        });
    }
    function reloadComments() {
      loadComments(currentPage.value);
    }

    function onPageChange(event) {
      loadComments(event.page + 1);
    }

    function initTopic(topicid, refetch = false) {
      // fetch topic
      projectStore
        .fetchTopic(topicid, refetch)
        .then(resp => {
          // check if we're allowed to access topic, in case of restricted_access
          if (resp.restrict_public_access == 0 || resp.restricted_access == 'allowed') {
            topic.value = resp;
            context.emit('loaded-topic-title', resp.id, resp.title);
          } else {
            // not allowed, reroute back to topics
            context.emit('show-topics');
          }
        })
        .catch(err => {
          console.log(err);
          // error, reroute back to topics
          context.emit('show-topics');
        });
    }

    function initThought(refetch = false) {
      // reset paging and other data
      comments.value = [];
      if (!refetch) {
        pager.value = {};
      }

      // fetch thought
      ThoughtRepository.getItem(thoughtid.value, 'all')
        .then(resp => {
          thought.value = resp;
          loadingThought.value = false;
          context.emit('loaded-thought-title', resp.id, resp.title);
          if (refetch) {
            reloadComments();
          } else {
            loadComments(1);
          }
          initTopic(resp.topic.id, refetch);
        })
        .catch(err => {
          console.log(err);
          // reroute back to topics on error (since we don't have a topicid)
          context.emit('show-topics');
        });
    }

    function emitCreateComment(params) {
      context.emit('create-comment', params);
    }
    function emitCreateThought(params) {
      context.emit('create-thought', params);
    }
    function emitEditThought() {
      if (thought.value && topic.value) {
        context.emit('edit-thought', {
          thought: thought.value,
          topic: topic.value,
        });
      }
    }

    // initialization
    onBeforeMount(() => {
      initThought();
    });

    //
    //-----------------WATCHERS------------------
    //
    watch(thoughtid, (newVal, oldVal) => {
      if (newVal !== oldVal) {
        initThought();
      }
    });
    watch(
      () => userStore.getState().user,
      (newUser, oldUser) => {
        // if logged in user changes make sure to refetch topic & thought
        if ((newUser == null && oldUser) || (newUser && oldUser == null) || newUser?.id != oldUser?.id) {
          const refetch = true;
          initThought(refetch);
        }
      }
    );

    return {
      // REFS
      pager,
      rows,
      currentPage,
      comments,
      thought,
      topic,
      loadingThought,
      loadingComments,
      commentFormElem,

      // COMPUTED
      totalCommentsCount,
      isLoading,

      // METHODS
      onPageChange,
      emitCreateComment,
      emitCreateThought,
      emitEditThought,
    };
  },

  components: {
    Paginator,
    Loading,
    Skeleton,
    CommentInList,
    ThoughtInCommentList,
    // CreateThoughtButton,
    CreateCommentButton,
    FollowThoughtButton,
  },
};
</script>

<style scoped lang="scss">
.comment-list {
  border: solid 1px #eee;
  border-top: none;
}
.forum-list-skeleton,
.no-comments-found {
  padding: 10px;
}
.controls {
  padding: 0.5rem;
  .control-el {
    margin-right: 0.5rem;
  }
}
.no-comments-found {
  border-top: solid 1px #eee;
}
</style>
